// Which wildlife record a studio collects at booking, keyed by the state the
// studio operates in. Texas studios sign the TPWD Wildlife Resource Document;
// Maine studios keep a per-transaction record for the state Taxidermy Report
// Book; Kentucky studios record the harvest confirmation number. Every
// document is stored on the job under the same file name so the WRD
// protections (kept on approval, not deletable) cover all of them.
//
// This module is free of react-pdf so the public booking page can import it.
// The PDF components themselves live in _components/CompliancePdfs/states.

import { NamedUserUID } from "@/app/@types/enums";
import { WRD_FILE_NAME } from "./collectionRequirements";

export type ComplianceDocType =
  | "wrd"
  | "maine-wildlife-record"
  | "kentucky-wildlife-record";

export const COMPLIANCE_DOC_FILE_NAME = WRD_FILE_NAME;

export interface ComplianceDocLabels {
  // Section heading on the booking form
  formTitle: string;
  // Printed title on the PDF
  pdfTitle: string;
  // The name used everywhere inside the app (settings, job form, drawer,
  // modal). One name for every state; only the document itself and the
  // hunter-facing booking heading carry the state's own title.
  shortName: string;
  // Label for the place the animal was taken
  harvestLocationLabel: string;
  harvestLocationRequired: boolean;
  // License Type choices, in display order
  licenseTypeOptions: { value: string; label: string }[];
  // Overrides the License Number label where the state needs it
  licenseNumberLabel?: string;
  // Settings > General toggle description
  settingsDescription: string;
}

export const CONFIRMATION_LICENSE_TYPE = "confirmation";

const HUNTING_LICENSE_OPTION = { value: "hunting", label: "Hunting License" };
const DRIVER_LICENSE_OPTION = { value: "driver", label: "Driver's License" };

// Same wording for every state; the studio never needs to know which
// state's document sits behind it.
const WILDLIFE_RECORD_SETTINGS_DESCRIPTION =
  "Enable collecting and in-person signing of Wildlife Records on jobs. For customization, reach out to support.";

export const complianceDocLabels: Record<ComplianceDocType, ComplianceDocLabels> =
  {
    wrd: {
      formTitle: "Wildlife Resource Documentation",
      pdfTitle: "Wildlife Resource Document",
      shortName: "Wildlife Record",
      harvestLocationLabel: "Ranch/Outfitter",
      harvestLocationRequired: true,
      licenseTypeOptions: [HUNTING_LICENSE_OPTION, DRIVER_LICENSE_OPTION],
      settingsDescription: WILDLIFE_RECORD_SETTINGS_DESCRIPTION,
    },
    "maine-wildlife-record": {
      formTitle: "Maine Wildlife Record",
      pdfTitle: "Maine Wildlife Record",
      shortName: "Wildlife Record",
      harvestLocationLabel: "Harvest Location",
      harvestLocationRequired: true,
      licenseTypeOptions: [HUNTING_LICENSE_OPTION, DRIVER_LICENSE_OPTION],
      settingsDescription: WILDLIFE_RECORD_SETTINGS_DESCRIPTION,
    },
    // Kentucky hunters check their game and get a confirmation number, often
    // the only number they have on them, and may not have a ranch or outfitter
    "kentucky-wildlife-record": {
      formTitle: "Kentucky Wildlife Record",
      pdfTitle: "Kentucky Wildlife Record",
      shortName: "Wildlife Record",
      harvestLocationLabel: "Harvest Location",
      harvestLocationRequired: false,
      licenseTypeOptions: [
        { value: CONFIRMATION_LICENSE_TYPE, label: "Confirmation Number" },
        HUNTING_LICENSE_OPTION,
        DRIVER_LICENSE_OPTION,
      ],
      licenseNumberLabel: "License / Confirmation #",
      settingsDescription: WILDLIFE_RECORD_SETTINGS_DESCRIPTION,
    },
  };

const docTypeByState: Record<string, ComplianceDocType> = {
  maine: "maine-wildlife-record",
  me: "maine-wildlife-record",
  kentucky: "kentucky-wildlife-record",
  ky: "kentucky-wildlife-record",
};

// Resolve the document from the studio's own state. Anything unmapped gets
// the WRD, which is what every studio collected before this existed.
export const getComplianceDocType = (
  state?: string | null
): ComplianceDocType => {
  const key = (state || "").trim().toLowerCase();
  return docTypeByState[key] || "wrd";
};

// Maine's seal number belongs to one animal, so it is kept on the mount's
// notes rather than on the job. The booking form writes it as the first line
// of the notes and the record pulls it back out from there, so a seal typed
// into the notes by hand on a walk-in job prints the same way.
export const SEAL_NOTE_PREFIX = "Seal / Tag #:";

export const sealNoteLine = (sealNumber: string): string =>
  `${SEAL_NOTE_PREFIX} ${sealNumber.trim()}`;

export const prependSealToNotes = (
  notes: string | null | undefined,
  sealNumber: string | null | undefined
): string => {
  const seal = sealNumber?.trim();
  if (!seal) return notes || "";
  const line = sealNoteLine(seal);
  return notes?.trim() ? `${line}\n${notes}` : line;
};

// Only a labelled line counts ("Seal #: 123", "Seal / Tag #: 123",
// "Seal: 123"), never the word seal inside ordinary notes.
const sealPattern =
  /^\s*seal(?:\s*\/\s*tag)?\s*(?:#|no\.?|number)?\s*[:#]\s*([A-Za-z0-9-]+)/im;

export const extractSealFromNotes = (
  notes: string | null | undefined
): string => {
  if (!notes) return "";
  const match = notes.match(sealPattern);
  return match?.[1] ?? "";
};

// Write seals collected in the drawer into their mounts' notes, keyed by line
// item uid. A mount whose notes already carry that seal is left alone.
export const applySealsToLineItems = <T extends { uid: string }>(
  lineItems: T[],
  sealsByLineItem: Record<string, string> | null | undefined
): T[] => {
  if (!sealsByLineItem || Object.keys(sealsByLineItem).length === 0) {
    return lineItems;
  }
  return lineItems.map((item) => {
    const seal = sealsByLineItem[item.uid]?.trim();
    if (!seal) return item;
    const notes = (item as { notes?: string | null }).notes;
    if (extractSealFromNotes(notes) === seal) return item;
    return { ...item, notes: prependSealToNotes(notes, seal) };
  });
};

// Taxidermy Designs record each animal's sex on their WRD. Like the seal it
// belongs to one animal, so it lives as a labelled line in the mount's notes
// and the record reads it back from there.
export const recordsSpecimenSex = (uid: string | null | undefined): boolean =>
  uid === NamedUserUID.TAXIDERMY_DESIGNS;

export const SPECIMEN_SEX_OPTIONS = ["Male", "Female", "Unknown"];

export const SEX_NOTE_PREFIX = "Sex:";

const sexPattern = /^\s*sex\s*:\s*(male|female|unknown)\b/im;
const sexLinePattern = /^\s*sex\s*:\s*(male|female|unknown)\b.*$\n?/gim;

export const extractSexFromNotes = (
  notes: string | null | undefined
): string => {
  const match = notes?.match(sexPattern)?.[1];
  if (!match) return "";
  return match[0].toUpperCase() + match.slice(1).toLowerCase();
};

// A changed sex replaces the old line rather than stacking a second one
export const setSexInNotes = (
  notes: string | null | undefined,
  sex: string | null | undefined
): string => {
  const value = sex?.trim();
  if (!value) return notes || "";
  const rest = (notes || "").replace(sexLinePattern, "");
  const line = `${SEX_NOTE_PREFIX} ${value}`;
  return rest.trim() ? `${line}\n${rest}` : line;
};

export const applySexesToLineItems = <T extends { uid: string }>(
  lineItems: T[],
  sexesByLineItem: Record<string, string> | null | undefined
): T[] => {
  if (!sexesByLineItem || Object.keys(sexesByLineItem).length === 0) {
    return lineItems;
  }
  return lineItems.map((item) => {
    const sex = sexesByLineItem[item.uid]?.trim();
    if (!sex) return item;
    const notes = (item as { notes?: string | null }).notes;
    if (extractSexFromNotes(notes) === sex) return item;
    return { ...item, notes: setSexInNotes(notes, sex) };
  });
};

// Everything the record drawer writes into mount notes
export const applyRecordNotesToLineItems = <T extends { uid: string }>(
  lineItems: T[],
  result: {
    sealsByLineItem?: Record<string, string> | null;
    sexesByLineItem?: Record<string, string> | null;
  }
): T[] =>
  applySexesToLineItems(
    applySealsToLineItems(lineItems, result.sealsByLineItem),
    result.sexesByLineItem
  );
